import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Chip, Divider, Typography } from '@mui/material';
import { SSOPolicyBadge } from './SSOStatusWidgets';
import { UI_TOKENS } from '../constants/uiTokens';
import { useTheme } from '../contexts/ThemeContext';
import { getStatusSurface } from '../utils/appearanceTokens';
import { oktaAPI } from '../services/api';
import type { SSOAttribute } from '../types';
import { resolveAttributesToReleaseLabels } from '../utils/ssoAttributeLabels';

type Props = {
  config: any;
  appRiskLevel?: string;
  /** When parent already loaded the catalog, pass it to avoid UUID flash. */
  attributeCatalog?: SSOAttribute[];
};

function resolveOktaAppId(config: any): string | undefined {
  return config?.okta_app_id
    || config?.manual_config?.okta_app_id
    || undefined;
}

function resolveEntraAppId(config: any): string | undefined {
  return config?.entra_app_id
    || config?.manual_config?.entra_app_id
    || undefined;
}

function resolveMetadataUrl(config: any): string | undefined {
  return config?.azure_metadata_url || config?.metadata_url || undefined;
}

function platformLabel(platform?: string): string | undefined {
  if (!platform) return undefined;
  const p = String(platform).toLowerCase();
  if (p === 'okta') return 'Okta';
  if (p === 'azure' || p === 'entra' || p === 'azuread') return 'Microsoft Entra ID';
  return platform;
}

const SSOConfigCompactDetails: React.FC<Props> = ({ config, appRiskLevel, attributeCatalog }) => {
  const { resolvedMode } = useTheme();
  const [fetchedCatalog, setFetchedCatalog] = useState<SSOAttribute[]>([]);

  const catalog = attributeCatalog?.length ? attributeCatalog : fetchedCatalog;

  useEffect(() => {
    if (attributeCatalog?.length) return;
    if (!(config.attributes_to_release?.length ?? 0)) return;
    let cancelled = false;
    oktaAPI
      .listSsoAttributes('application')
      .then(res => {
        if (!cancelled) setFetchedCatalog(res.data?.data || []);
      })
      .catch(() => {
        if (!cancelled) setFetchedCatalog([]);
      });
    return () => {
      cancelled = true;
    };
  }, [attributeCatalog, config.attributes_to_release]);

  const attributeLabels = useMemo(
    () => resolveAttributesToReleaseLabels(config.attributes_to_release, catalog),
    [config.attributes_to_release, catalog],
  );

  const provisionedSurface = getStatusSurface('success', resolvedMode);
  const dr = (label: string, value: string | number | null | undefined) =>
    value ? (
      <Box key={label} sx={{ display: 'flex', gap: 1, mb: 0.75 }}>
        <Typography sx={{ fontSize: UI_TOKENS.text.buttonSm, color: 'text.secondary', minWidth: 160 }}>{label}</Typography>
        <Typography sx={{ fontSize: UI_TOKENS.text.buttonSm, fontWeight: 500, wordBreak: 'break-all' }}>{value}</Typography>
      </Box>
    ) : null;

  const oktaAppId = resolveOktaAppId(config);
  const entraAppId = resolveEntraAppId(config);
  const metadataUrl = resolveMetadataUrl(config);
  const platform = platformLabel(config.platform);
  const isOkta = String(config.platform || '').toLowerCase() === 'okta';
  const grantType = String(config.grant_type || config.manual_config?.grant_type || '').toLowerCase();
  const hasClientId = !!(config.client_id || config.manual_config?.client_id);
  const requestsEntraOnApprove = !!(
    config.manual_config?.request_entra_create_on_approve ||
    config.manual_config?.request_entra_app_create ||
    config.manual_config?.vysor_cc_create_request ||
    (config.sso_type === 'OIDC' && grantType === 'client_credentials' && !hasClientId)
  );
  const hasProvisioned = ['Approved', 'Completed'].includes(config.status)
    && !!(config.client_id || config.client_secret || metadataUrl || config.certificate_expiration_date || oktaAppId || entraAppId);

  return (
    <Box>
      <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary', display: 'block', mb: 1 }}>
        Configuration Details
      </Typography>

      {dr('Connection Name', config.connection_name ? `sso-${config.connection_name}` : undefined)}
      {dr('Platform / IdP', platform)}
      {dr('SSO Type', config.sso_type)}
      {dr('Status', config.status)}
      {dr('Contact Email', config.contact_email)}
      {config.sso_type === 'SAML' && dr('Entity ID', config.entity_id)}
      {config.sso_type === 'SAML' && dr('ACS URL', config.acs_url)}
      {config.sso_type === 'SAML' && dr('Name ID Format', config.name_id_format)}
      {config.sso_type === 'OIDC' && dr('Client ID', config.client_id || config.manual_config?.client_id)}
      {config.sso_type === 'OIDC' && !hasClientId && requestsEntraOnApprove && (
        <Box sx={{ display: 'flex', gap: 1, mb: 0.75 }}>
          <Typography sx={{ fontSize: UI_TOKENS.text.buttonSm, color: 'text.secondary', minWidth: 160 }}>Client ID</Typography>
          <Typography sx={{ fontSize: UI_TOKENS.text.buttonSm, fontStyle: 'italic', color: 'text.secondary' }}>
            Will be created in Entra after SSO approval
          </Typography>
        </Box>
      )}
      {config.sso_type === 'OIDC' && dr('Grant Type', config.grant_type || config.manual_config?.grant_type)}
      {config.sso_type === 'OIDC' && (config.redirect_uris?.length ?? 0) > 0 && dr('Redirect URIs', config.redirect_uris.join(', '))}
      {config.sso_type === 'OIDC' && (config.scopes?.length ?? 0) > 0 && dr('Scopes', config.scopes.join(', '))}
      {config.sso_type === 'OIDC' && dr(
        'OIDC Discovery',
        config.oidc_discovery_endpoint || config.manual_config?.oidc_discovery_endpoint
      )}
      {(config.attributes_to_release?.length ?? 0) > 0 && (
        <Box sx={{ display: 'flex', gap: 1, mb: 0.75 }}>
          <Typography sx={{ fontSize: UI_TOKENS.text.buttonSm, color: 'text.secondary', minWidth: 160 }}>Attributes to Release</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {attributeLabels.map((label: string, i: number) => (
              <Chip key={`${label}-${i}`} label={label} size="small" variant="outlined" sx={{ height: 20, fontSize: UI_TOKENS.text.helper }} />
            ))}
          </Box>
        </Box>
      )}
      {Array.isArray(config.attribute_statements) && config.attribute_statements.length > 0 && (
        <Box sx={{ mb: 0.75 }}>
          <Typography sx={{ fontSize: UI_TOKENS.text.buttonSm, color: 'text.secondary', mb: 0.5 }}>
            {isOkta ? 'Okta Attribute Statements' : 'Attribute Statements'}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, ml: 0 }}>
            {config.attribute_statements.map((stmt: any, i: number) => {
              const formatLabel = (() => {
                if (typeof stmt.namespace !== 'string') return '';
                if (stmt.namespace.endsWith(':basic')) return 'Basic';
                if (stmt.namespace.endsWith(':uri')) return 'URI';
                if (stmt.namespace.endsWith(':unspecified')) return 'Unspecified';
                return stmt.namespace;
              })();
              const valueText = Array.isArray(stmt.values) ? stmt.values.join(' | ') : '';
              const isGroup = stmt.type === 'GROUP';
              return (
                <Box key={i} sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5, p: 0.75, borderRadius: 1, border: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
                  <Chip
                    size="small"
                    label={isGroup ? 'Group' : 'Expression'}
                    color={isGroup ? 'warning' : 'info'}
                    variant="outlined"
                    sx={{ height: 18, fontSize: UI_TOKENS.text.labelXs, '& .MuiChip-label': { px: 0.75 } }}
                  />
                  <Typography sx={{ fontSize: UI_TOKENS.text.buttonSm, fontWeight: 700 }}>{stmt.name}</Typography>
                  {formatLabel && (
                    <Typography sx={{ fontSize: UI_TOKENS.text.labelXs, color: 'text.secondary' }}>· {formatLabel}</Typography>
                  )}
                  <Typography sx={{ fontSize: UI_TOKENS.text.labelXs, color: 'text.disabled', mx: 0.5 }}>→</Typography>
                  <Typography component="code" sx={{ fontSize: UI_TOKENS.text.labelXs, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', color: 'text.primary', wordBreak: 'break-all' }}>
                    {isGroup && stmt.filterType ? `${stmt.filterType} ${stmt.filterValue || ''}` : valueText}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
      {dr('Data Classification', config.data_classification)}
      {appRiskLevel && dr('Risk Level', appRiskLevel)}
      {config.expected_user_count != null && config.expected_user_count > 0 && dr('Expected User Count', config.expected_user_count)}
      {dr('Business Justification', config.business_justification)}
      {dr('Technical Notes', config.technical_notes)}
      {config.x509_certificate && config.x509_certificate !== '***REDACTED***' && (
        <Box sx={{ mb: 0.75 }}>
          <Typography sx={{ fontSize: UI_TOKENS.text.buttonSm, color: 'text.secondary', mb: 0.25 }}>X.509 Certificate</Typography>
          <Typography component="pre" sx={{ fontSize: UI_TOKENS.text.labelXs, bgcolor: 'grey.50', p: 1, borderRadius: 1, overflow: 'auto', maxHeight: 80 }}>
            {config.x509_certificate}
          </Typography>
        </Box>
      )}

      {appRiskLevel && ['Low', 'Medium', 'High', 'Critical'].includes(appRiskLevel) && (
        <Box sx={{ mt: 1.5 }}>
          <Divider sx={{ mb: 1.5 }} />
          <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary', display: 'block', mb: 1 }}>
            Security Policy
          </Typography>
          <SSOPolicyBadge riskLevel={appRiskLevel} />
        </Box>
      )}

      {hasProvisioned && (
        <Box sx={{ mt: 1.5 }}>
          <Divider sx={{ mb: 1.5 }} />
          <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'success.main', display: 'block', mb: 1 }}>
            Provisioned Credentials
          </Typography>
          <Box sx={{ p: 1.5, bgcolor: provisionedSurface.bg, borderRadius: 1.5, border: '1px solid', borderColor: provisionedSurface.border }}>
            {oktaAppId && dr('Okta App ID', oktaAppId)}
            {entraAppId && dr('Entra App ID', entraAppId)}
            {config.sso_type === 'OIDC' && (
              <>
                {(config.client_id || config.manual_config?.client_id) &&
                  dr('Client ID', config.client_id || config.manual_config?.client_id)}
                {config.client_secret ? (
                  dr('Client Secret', config.client_secret)
                ) : (
                  <Box sx={{ display: 'flex', gap: 1, mb: 0.75 }}>
                    <Typography sx={{ fontSize: UI_TOKENS.text.buttonSm, color: 'text.secondary', minWidth: 160 }}>Client Secret</Typography>
                    <Typography sx={{ fontSize: UI_TOKENS.text.buttonSm, fontStyle: 'italic', color: 'text.secondary' }}>Generating... check back shortly</Typography>
                  </Box>
                )}
                {config.client_secret_expires_at &&
                  dr('Secret Expires', new Date(config.client_secret_expires_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }))}
                {(config.oidc_discovery_endpoint || config.manual_config?.oidc_discovery_endpoint) &&
                  dr('OIDC Discovery', config.oidc_discovery_endpoint || config.manual_config?.oidc_discovery_endpoint)}
              </>
            )}
            {config.sso_type === 'SAML' && (
              <>
                {metadataUrl && dr(isOkta ? 'Okta Metadata URL' : 'IdP Metadata URL', metadataUrl)}
                {config.idp_initiated_url && dr('IdP Initiated URL', config.idp_initiated_url)}
                {config.certificate_expiration_date &&
                  dr('Certificate Expiry', new Date(config.certificate_expiration_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }))}
              </>
            )}
          </Box>
        </Box>
      )}

      {config.review_notes && (
        <Box sx={{ mt: 1.5 }}>
          <Divider sx={{ mb: 1.5 }} />
          <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary', display: 'block', mb: 0.75 }}>
            Review Notes
          </Typography>
          <Alert severity="info" sx={{ fontSize: UI_TOKENS.text.bodySm, py: UI_TOKENS.menu.itemPaddingY }}>
            {config.review_notes}
          </Alert>
        </Box>
      )}
    </Box>
  );
};

export default SSOConfigCompactDetails;
